import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { GameRoomStatus, Status } from '@/redux/types/room'

type BadgeVariant =
  | 'ongoing'
  | 'waiting'
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'invite'
  | 'completed'
  | 'terminated'
  | 'paused'

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80',
        outline: 'text-foreground',
        ongoing:
          'bg-emerald-500/90 text-white hover:bg-emerald-600 border-transparent shadow-sm',
        waiting:
          'bg-blue-500/90 text-white hover:bg-blue-600 border-transparent shadow-sm',
        pending:
          'bg-amber-500/90 text-white hover:bg-amber-600 border-transparent shadow-sm',
        accepted:
          'bg-emerald-500/90 text-white hover:bg-emerald-600 border-transparent shadow-sm',
        rejected:
          'bg-red-500/90 text-white hover:bg-red-600 border-transparent shadow-sm',
        invite:
          'bg-amber-500/90 text-white hover:bg-amber-600 border-transparent shadow-sm',
        completed:
          'bg-red-500/90 text-white hover:bg-red-600 border-transparent shadow-sm',
        terminated:
          'bg-red-500/90 text-white hover:bg-red-600 border-transparent shadow-sm',
        paused:
          'bg-gray-500/90 text-white hover:bg-gray-600 border-transparent shadow-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

const statusDotVariants = cva('size-2.5 rounded-full', {
  variants: {
    variant: {
      ongoing: 'bg-white animate-pulse',
      waiting: 'bg-white',
      pending: 'bg-white',
      accepted: 'bg-white',
      rejected: 'bg-white',
      invite: 'bg-white',
      terminated: 'bg-white',
      completed: 'bg-white',
      paused: 'bg-white',
    },
  },
  defaultVariants: {
    variant: 'ongoing',
  },
})

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  status: GameRoomStatus | Status
  label?: string
}

const labelMap: { [key: string]: string } = {
  ongoing: 'Ongoing Game',
  waiting: 'Waiting',
  pending: 'Pending Invite',
  accepted: 'Accepted',
  rejected: 'Rejected',
  terminated: 'Terminated',
  invite: 'Invite',
  completed: 'Game Ended',
  paused: 'Paused',
}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div
      className={cn(
        badgeVariants({ variant: variant as BadgeVariant }),
        className
      )}
      {...props}
    />
  )
}

export function StatusBadge({
  className,
  status,
  label,
  ...props
}: StatusBadgeProps) {
  const variantMap: { [key: string]: BadgeVariant } = {
    [GameRoomStatus.NOT_STARTED]: 'waiting',
    [GameRoomStatus.IN_PROGRESS]: 'ongoing',
    [GameRoomStatus.COMPLETED]: 'completed',
    [GameRoomStatus.PAUSED]: 'paused',
    [Status.Pending]: 'pending',
    [Status.Accepted]: 'accepted',
    [Status.Rejected]: 'rejected',
    [Status.Invite]: 'invite',
    [Status.Terminated]: 'terminated',
  }

  const variant = variantMap[status] || 'waiting'
  const displayLabel = label || (variant && labelMap[variant]) || ''

  return (
    <Badge
      variant={variant}
      className={cn(
        'gap-2 border-transparent backdrop-blur-md shadow-md',
        className
      )}
      {...props}
    >
      <span
        className={statusDotVariants({ variant: variant as BadgeVariant })}
      />
      {displayLabel}
    </Badge>
  )
}

export { Badge, badgeVariants }
